'use client'
import { memo, useEffect, useMemo, useRef, useState, type FC } from 'react'
import styled from 'styled-components'
import { motion, useAnimation, useInView } from 'motion/react'
import { useDeviceType, useMount, useTranslator } from '@/hooks'
import { IS_MMF_STORE } from '@/utils'
import { designGrid, getTransition, mq, presets, vw } from '@/styles'
import { Logos } from './Logos'

type Props = {
  brands: Array<{
    logo: { src: string },
    slug: { [key: string]: string }
  }>
}

const getNewLogoArray = (prev, all) => {
  const available = all.filter(img => !prev.includes(img))
  if (!available.length) return prev

  const replacement = available[Math.floor(Math.random() * available.length)]
  const indexToReplace = Math.floor(Math.random() * prev.length)

  return prev.map((item, i) => i === indexToReplace ? replacement : item)
}


// DIFFERENT MMF FROM HM SLIDER TO MIX into one
const headingId = 'brands'
export const SectionBrands:FC<Props> = memo(({ brands }) => {
  const sectionRef = useRef(null)
  const { isMobile } = useDeviceType()
  const logoLimit = useMemo(() => isMobile ? 3 : 5, [isMobile])
  const initialArray = useMemo(() => brands.slice(0, logoLimit), [brands, logoLimit])
  const [logoArray, setLogoArray] = useState(initialArray)
  const inView = useInView(sectionRef, { once: true, amount: 1 })
  const controls = useAnimation()
  const { t } = useTranslator()
  const isMounted = useMount()

  useEffect(() => {
    if(inView) {
      controls.start('animate')
    }
  }, [controls, inView])

  useEffect(() => {
    if (brands.length <= initialArray.length) return

    const interval = setInterval(() => {
      setLogoArray(prev => getNewLogoArray(prev, brands))
    }, 3200)

    return () => clearInterval(interval)
  }, [brands, initialArray])

  return isMounted && (
    <Section ref={sectionRef} aria-labelledby={headingId} >
      <h2 id={headingId}>{t('project.section_brands.title')}</h2>
      <BrandWrapper variants={wrapperVariants} animate={isMounted ? 'active' : 'inactive'} initial='inactive' $isMmf={IS_MMF_STORE}>
        <Logos data={logoArray.slice(0, isMobile ? 3 : 5)} />
      </BrandWrapper>
    </Section>
  )
})


const Section = styled.section`
  ${designGrid({})}
  margin-bottom: ${vw(73, 'mobile')};
  margin-top: ${vw(80, 'mobile')};
  overflow: hidden;

  h2 {
    ${presets.font20_1};
    grid-row: 1 / span 1;
    grid-column: 1 / span 6;
    text-align: center;
    margin-bottom: ${vw(28, 'mobile')};
  }

  ${mq.greaterThan('tablet')} {
    margin-bottom: ${vw(90, 'desktop')};
    margin-top: ${vw(150, 'desktop')};

    h2 {
      grid-row: 1 / span 1;
      grid-column: 1 / span 12;
      margin-bottom: ${vw(40, 'desktop')};
    }
  }

  ${mq.greaterThan('desktop')} {
    margin-bottom: 90px;
    margin-top: 150px;
  }
`

const BrandWrapper = styled(motion.div) <{ $isMmf: boolean }>`
  display: grid;
  grid-column: 1 / span 6;
  grid-row: 2 / span 2;
  grid-template-columns: repeat(3, 1fr);
  height: ${vw(57, 'mobile')};
  margin: 0 ${vw(20, 'mobile')};

  article,
  a,
  figure {
    height: inherit;
  }

  article {
    padding: ${vw(5, 'mobile')} ${vw(15, 'mobile')};
  }

  ${({ $isMmf }) => $isMmf ? `
    img {
      filter:invert(49%) sepia(37%) saturate(691%) hue-rotate(313deg) brightness(74%) contrast(127%);
    }
  `
    : `

    img {
      filter:invert(49%) sepia(37%) saturate(331%) hue-rotate(69deg) brightness(92%) contrast(97%);
    }
  `}

  img {
    height: 100%;
    object-fit: contain;
  }

  ${mq.greaterThan('tablet')} {
    grid-column: 2 / span 10;
    grid-gap: 50px;
    grid-row: 2 / span 1;
    grid-template-columns: repeat(5, 1fr);
    height: ${vw(108, 'desktop')};

    article {
      margin: 0 ${vw(30, 'desktop')};
      padding: 0;
    }
  }
`

const wrapperVariants = {
  inactive: {
    opacity: 0,
    ...getTransition(1),

  },
  active: {
    opacity: 1,

    transition: {
      ...getTransition(1),
      delay: 1.5
    }
  }
}

